import type { CoreOrganization } from '@/lib/zifekCore';

interface Props {
  organizations: CoreOrganization[];
  tenantNameById: Record<number, string>;
  locationCount: Record<number, number>;
  memberCount: Record<number, number>;
  ownerLabel: Record<number, string>;
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-accent-100 text-accent-900',
  inactive: 'bg-foreground-100 text-foreground-600',
};

export default function OrganizationsPanel({
  organizations,
  tenantNameById,
  locationCount,
  memberCount,
  ownerLabel,
}: Props) {
  if (organizations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-building-2-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-600">Aucune organisation.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {organizations.map((o) => {
        const active = o.status === 'active';
        return (
          <div key={o.id} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-2-line text-lg"></i>
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-foreground-950 truncate">
                    {o.commercial_name || o.legal_name || `Organisation #${o.id}`}
                  </h3>
                  <span className="text-[11px] text-foreground-400">Tenant #{o.tenant_id ?? '—'} · {tenantNameById[o.tenant_id ?? -1] || '—'}</span>
                </div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_STYLES[o.status] || 'bg-foreground-100 text-foreground-600'}`}>
                {active ? 'Actif' : o.status}
              </span>
            </div>

            <div className="text-xs text-foreground-500 space-y-1 mb-4">
              <div className="flex items-center gap-1.5">
                <i className="ri-price-tag-3-line text-foreground-400"></i>
                <span className="truncate">{o.activity_category || o.business_type || 'Activité non définie'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-map-pin-line text-foreground-400"></i>
                <span className="truncate">{o.country || 'Pays non défini'} · {o.default_currency || 'MAD'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-user-line text-foreground-400"></i>
                <span className="truncate">Propriétaire : {ownerLabel[o.owner_user_id ?? -1] || '—'}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-3 border-t border-background-200/60">
              <span className="flex items-center gap-1.5 text-xs text-foreground-600 bg-background-100 rounded-full px-2.5 py-1">
                <i className="ri-store-2-line text-secondary-600"></i>
                {locationCount[o.id] || 0} établissement(s)
              </span>
              <span className="flex items-center gap-1.5 text-xs text-foreground-600 bg-background-100 rounded-full px-2.5 py-1">
                <i className="ri-group-line text-accent-600"></i>
                {memberCount[o.id] || 0} membre(s)
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}